import React, { useEffect, useState, Suspense, lazy, memo, createElement } from 'react';
import { Check, X, ShieldCheck, Star, Target, Flame, ChevronDown, ChevronLeft, ChevronRight, Quote, Clock, Zap, BookOpen, Brain, Map, ClipboardCheck, Calendar, FileQuestion, Layers, Gift, AlertCircle, Infinity as InfinityIcon, Rocket, Stethoscope, GraduationCap } from 'lucide-react';
const CommentsSection = lazy(() => import('./components/CommentsSection').then(m => ({
  default: m.CommentsSection
})));
const SocialProofPopup = lazy(() => import('./components/SocialProofPopup').then(m => ({
  default: m.SocialProofPopup
})));
const CTAButton = ({
  children,
  subtext,
  scrollTo
}: {
  children: React.ReactNode;
  subtext?: string;
  scrollTo?: string;
}) => {
  const handleClick = () => {
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="flex flex-col items-center w-full max-w-xl mx-auto mt-8 mb-6" data-id="element-0">
      <button onClick={handleClick} className="w-full group relative inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-white transition-all duration-200 bg-gradient-to-b from-emerald-400 to-emerald-600 border-b-4 border-emerald-800 rounded-xl hover:from-emerald-300 hover:to-emerald-500 hover:-translate-y-1 active:translate-y-0 active:border-b-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 focus:ring-offset-dark-900 shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_60px_rgba(16,185,129,0.5)]" data-id="element-1">
        <span className="flex items-center gap-2 text-xl tracking-wide uppercase text-shadow-sm" data-id="element-2">
          {children}
        </span>
      </button>
      {subtext && <p className="mt-3 text-sm font-medium text-gray-400 flex items-center gap-2 text-center" data-id="element-3">
          {subtext}
        </p>}
    </div>;
};
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/mG4ZcxiedsEXYhVxzvinwx/mocap_humana_(1).webp';
const CHECKOUT_URL = 'https://recursopack.com/cart/40998269780017:1';
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselSlides = [{
    title: 'División del Esqueleto · Axial y Apendicular',
    subtitle: 'Sistema Esquelético · Página 23',
    image: 'https://cdn.magicpatterns.com/uploads/kgztbYirAnPspJm9AH3Q3w/1_anatomia.webp'
  }, {
    title: 'Huesos del Tronco · Costillas y Columna',
    subtitle: 'Sistema Esquelético · Página 42',
    image: 'https://cdn.magicpatterns.com/uploads/dqCCnkCiSqb11FfqAZyFu4/2_anatomia.webp'
  }, {
    title: 'Principales Músculos del Cuerpo Humano',
    subtitle: 'Sistema Muscular · Página 70',
    image: 'https://cdn.magicpatterns.com/uploads/kTmbUJa9PiDff6hPwJEP9s/3_anatomia.webp'
  }, {
    title: 'Anatomía del Corazón · Cámaras y Vasos',
    subtitle: 'Sistema Cardiovascular · Página 86',
    image: 'https://cdn.magicpatterns.com/uploads/p33T7mHPHrSiDoPNaqrgPT/4_anatomia.webp'
  }, {
    title: 'Órganos del Sistema Respiratorio',
    subtitle: 'Sistema Respiratorio · Página 112',
    image: 'https://cdn.magicpatterns.com/uploads/kqoMrKSyiHEnkzpiQ6KDwT/5_anatomia.webp'
  }, {
    title: 'Sistema Reproductor Femenino',
    subtitle: 'Aparato Reproductor · Página 138',
    image: 'https://cdn.magicpatterns.com/uploads/4iF4HqngxPSreHtF3y5tbX/6_anatomia.webp'
  }];
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % carouselSlides.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + carouselSlides.length) % carouselSlides.length);
  useEffect(() => {
    const loadPixel = () => {
      if ((window as any).fbq) return;
      const f = window as any;
      const n: any = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(t);
      n('init', '846575091369965');
      n('track', 'PageView');
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadPixel, {
        timeout: 3000
      });
    } else {
      setTimeout(loadPixel, 2500);
    }
  }, []);
  const faqs = [{
    q: '¿Todo está realmente en PDF?',
    a: 'Sí. El 100% del Mega Pack es en formato PDF. Podés descargarlo, imprimirlo o usarlo en tu tablet, computadora o celular desde el momento de la compra.'
  }, {
    q: '¿Es adecuado para mi carrera?',
    a: 'Sí. El Mega Pack fue diseñado para estudiantes de medicina, enfermería, fisioterapia, kinesiología, nutrición, odontología y cualquier carrera de ciencias de la salud que incluya anatomía humana en su plan de estudios.'
  }, {
    q: '¿Necesito conocimientos previos para usarlo?',
    a: 'No. El sistema está diseñado para funcionar tanto si estás comenzando anatomía desde cero como si ya tenés conocimientos y necesitás organizar y reforzar lo que sabés.'
  }, {
    q: '¿Cuándo recibo los materiales?',
    a: 'Inmediatamente después de confirmar tu pago recibís un correo con el acceso completo a todos los recursos del Mega Pack. Sin esperas.'
  }, {
    q: '¿Qué pasa si no estoy satisfecho?',
    a: 'Tenés 7 días desde la compra para pedir el reembolso completo sin ninguna pregunta ni trámite complicado. El riesgo es 100% nuestro.'
  }, {
    q: '¿Por qué el precio es tan accesible?',
    a: 'Creamos este producto para que cualquier estudiante de salud en Latinoamérica pueda acceder a una herramienta de estudio de nivel profesional sin que el precio sea una barrera. Queremos que más estudiantes aprueben con confianza.'
  }];
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const bonuses = [{
    icon: BookOpen,
    title: 'Guía Principal · 192 páginas',
    subtitle: 'Los 12 sistemas del cuerpo humano',
    desc: 'La base completa de tu estudio de anatomía. Cada sistema explicado con ilustraciones detalladas, descripciones claras y conexiones entre sistemas.',
    bullets: ['Sistema tegumentario, muscular y cardíaco', 'Sistema linfático, respiratorio y digestivo', 'Sistema reproductor femenino y masculino', 'Sistema excretor, nervioso, sensorial y endocrino', 'Ilustraciones detalladas por sistema'],
    tag: 'GUÍA PRINCIPAL',
    value: '$27 USD'
  }, {
    icon: Map,
    title: 'Bono #1 · Mapa Maestro de Anatomía',
    subtitle: 'Visión integrada de los 12 sistemas',
    desc: 'Entendé el cuerpo humano como un todo. El mapa que conecta todos los sistemas entre sí y te da el panorama completo antes de estudiar los detalles.',
    bullets: ['Mapa de conexiones entre sistemas', 'Niveles de organización del cuerpo', 'Ruta ideal de estudio', 'Ejemplos clínicos de conexión', 'Planificación semanal incluida'],
    tag: 'BONO #1',
    value: '$15 USD'
  }, {
    icon: BookOpen,
    title: 'Bono #2 · Guía Express de Términos',
    subtitle: 'El vocabulario que más se evalúa',
    desc: 'Dominá el lenguaje de la anatomía desde el primer día. Términos de dirección, prefijos, sufijos y glosario completo explicados de forma simple y visual.',
    bullets: ['Posición anatómica estándar', 'Términos de dirección con ejemplos', 'Planos anatómicos explicados', 'Prefijos y sufijos más frecuentes', 'Glosario completo de términos esenciales'],
    tag: 'BONO #2',
    value: '$15 USD'
  }, {
    icon: ClipboardCheck,
    title: 'Bono #3 · Checklist por Sistemas',
    subtitle: 'Verificá que realmente lo entendiste',
    desc: 'Un sistema de control punto por punto para cada sistema del cuerpo. Nunca más te vas a dar cuenta en el examen que te faltó repasar algo importante.',
    bullets: ['13 checklists completos', '336 puntos de verificación', 'Seguimiento de progreso por sistema', 'Sección de observaciones personales', 'Indicador de nivel de dominio'],
    tag: 'BONO #3',
    value: '$15 USD'
  }, {
    icon: Calendar,
    title: 'Bono #4 · Plan de 14 Días',
    subtitle: 'Tu ruta de estudio día por día',
    desc: 'El plan de estudio estructurado que te lleva desde el día 1 hasta el examen con una secuencia lógica, sesiones claras y momentos de repaso espaciado incluidos.',
    bullets: ['14 días de estudio estructurado', 'Sesiones de 45 a 60 minutos por día', 'Repaso espaciado incorporado', 'Días de refuerzo y consolidación', 'Checklist diario de cumplimiento'],
    tag: 'BONO #4',
    value: '$15 USD'
  }, {
    icon: FileQuestion,
    title: 'Bono #5 · Autoevaluación Anatómica',
    subtitle: 'Detectá tus vacíos antes del examen',
    desc: '291 preguntas con respuestas completas organizadas por sistema. El recurso que convierte la lectura en aprendizaje real y te muestra exactamente qué reforzar.',
    bullets: ['291 preguntas organizadas por sistema', 'Respuestas detalladas para cada pregunta', 'Sistema de 4 niveles de dominio', 'Simulacro completo de examen', 'Análisis de vacíos y plan de mejora'],
    tag: 'BONO #5',
    value: '$15 USD'
  }, {
    icon: Zap,
    title: 'Bono #6 · Fichas de Memorización Rápida',
    subtitle: 'Repasá todo en minutos antes del examen',
    desc: '25 fichas visuales con los puntos más importantes de cada sistema. Tu herramienta de los últimos días para activar la memoria rápido y llegar con todo fresco.',
    bullets: ['25 fichas visuales completas', 'Una ficha de 60 segundos por sistema', 'Tabla maestra de hormonas', 'Los 10 errores más frecuentes corregidos', 'Mini resumen de bolsillo de los 12 sistemas'],
    tag: 'BONO #6',
    value: '$15 USD'
  }, {
    icon: Gift,
    title: 'Bono Sorpresa',
    subtitle: 'Desbloqueado con tu compra',
    desc: 'Un recurso adicional especialmente seleccionado para potenciar tu estudio. Lo descubrís apenas confirmás tu acceso al Mega Pack.',
    bullets: ['Acceso inmediato con tu compra', 'Complemento directo de la guía principal', '100% PDF listo para usar'],
    tag: 'BONO SORPRESA',
    value: '$15 USD'
  }];
  const testimonialsTop = [{
    quote: 'Por fin algo que funciona en la práctica. En la primera semana encontré 3 vacíos en mi estudio que antes no veía. Aprobé anatomía con 9.',
    name: 'María G.',
    role: 'Estudiante de Enfermería · Buenos Aires'
  }, {
    quote: 'Reduje mi tiempo de preparación un 80% en el primer mes. No creía que un PDF podía cambiar tanto mi forma de estudiar.',
    name: 'Lucas T.',
    role: 'Estudiante de Medicina · Córdoba'
  }, {
    quote: 'Por fin me liberé de estudiar sin rumbo cada semana. Este sistema realmente funciona y se nota desde el primer día.',
    name: 'Valentina R.',
    role: 'Estudiante de Fisioterapia · Bogotá'
  }];
  const differentiators = [{
    icon: Target,
    title: 'FUNCIONA EN LA PRÁCTICA',
    desc: 'Cada página es una herramienta lista para usar en tu próxima sesión de estudio. No un manual académico lleno de texto.'
  }, {
    icon: Rocket,
    title: 'EMPEZÁ DESDE AHORA',
    desc: 'Sin aprender software. Sin configuraciones. Abrís el PDF y empezás a estudiar al instante.'
  }, {
    icon: Layers,
    title: 'COMPLETO EN TODAS LAS ÁREAS',
    desc: 'Cubre todo: guía ilustrada, vocabulario, checklists, plan de repaso, autoevaluación y fichas de memorización rápida.'
  }, {
    icon: InfinityIcon,
    title: 'TUYO PARA SIEMPRE',
    desc: 'Pago único. Sin cargos mensuales. Usalo cuando quieras en cualquier dispositivo, durante toda tu carrera.'
  }];
  const TestimonialCard = ({
    quote,
    name,
    role
  }: {
    quote: string;
    name: string;
    role: string;
  }) => <div className="glass-panel p-6 md:p-7 rounded-2xl border-white/10 relative h-full flex flex-col" data-id="element-4">
      <Quote className="w-8 h-8 text-gold-400/40 mb-3 shrink-0" data-id="element-5" />
      <p className="text-gray-200 leading-relaxed mb-5 flex-1 italic" data-id="element-6">
        "{quote}"
      </p>
      <div className="flex items-center gap-1 text-gold-400 mb-2" data-id="element-7">
        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-8" />)}
      </div>
      <p className="text-white font-bold text-sm" data-id="element-9">— {name}</p>
      <p className="text-gray-500 text-xs" data-id="element-10">{role}</p>
    </div>;
  return <div className="min-h-screen bg-dark-900 text-gray-300 selection:bg-gold-500 selection:text-black pb-20 overflow-x-hidden" data-id="element-11">
      {/* Banner */}
      <div className="w-full bg-gradient-to-r from-red-700 via-red-600 to-red-700 py-3 px-4 flex justify-center items-center z-50" data-id="element-12">
        <div className="flex items-center gap-3 max-w-4xl mx-auto text-center" data-id="element-13">
          <div className="w-2.5 h-2.5 rounded-full bg-white animate-pulse-fast shrink-0 shadow-[0_0_10px_rgba(255,255,255,0.8)]" data-id="element-14"></div>
          <p className="text-xs md:text-sm font-bold tracking-wider text-white uppercase" data-id="element-15">
            OFERTA DE LANZAMIENTO · SOLO PARA LOS PRIMEROS 200 ESTUDIANTES ·
            PRECIO BLOQUEADO A $19.90 USD
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-20" data-id="element-16">
        {/* Hero */}
        <div className="text-center space-y-5 md:space-y-8 mb-8 md:mb-12" data-id="element-17">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 font-medium text-xs md:text-sm" data-id="element-18">
            <AlertCircle className="w-4 h-4 shrink-0" data-id="element-19" />
            ATENCIÓN: ESTUDIANTES DE CIENCIAS DE LA SALUD
          </div>

          <h1 className="text-3xl md:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight" data-id="element-20">
            Estudiá todo el año con{' '}
            <span className="gold-gradient-text" data-id="element-21">un solo sistema</span>
          </h1>

          <div className="max-w-3xl mx-auto space-y-4 md:space-y-6" data-id="element-22">
            <div className="relative pl-5 md:pl-6 py-2 text-left" data-id="element-23">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-gold-400 to-gold-600 rounded-full" data-id="element-24"></div>
              <p className="text-base md:text-xl text-gray-300 leading-relaxed font-medium" data-id="element-25">
                El primer Mega Pack completo{' '}
                <span className="text-white font-bold" data-id="element-26">"Abrí y Estudiá"</span> —
                con guías ilustradas, fichas de memorización y planes de repaso
                para los{' '}
                <span className="text-white font-bold" data-id="element-27">
                  12 sistemas del cuerpo humano
                </span>
                . Recuperá tu tiempo y aprobá con confianza.
              </p>
            </div>
            <p className="text-lg md:text-2xl font-bold text-gold-400" data-id="element-28">
              Mega Pack · Anatomía Humana
            </p>
          </div>
        </div>

        {/* Product image + social proof */}
        <div className="flex flex-col items-center mb-16" data-id="element-29">
          <div className="relative w-full max-w-md sm:max-w-lg md:max-w-2xl mx-auto mb-8 aspect-[4/3]" data-id="element-30">
            <div className="absolute inset-0 bg-gold-500/10 blur-[100px] rounded-full pointer-events-none blur-glow" data-id="element-31"></div>
            <img src={HERO_IMAGE} alt="Mega Pack · Anatomía Humana" width={1200} height={900} fetchPriority="high" decoding="sync" loading="eager" className="relative z-10 w-full h-full object-contain drop-shadow-2xl md:hover:scale-[1.02] transition-transform duration-500" data-id="element-32" />
          </div>

          <div className="flex flex-col items-center space-y-3 mb-8" data-id="element-33">
            <div className="flex items-center gap-1 text-gold-400" data-id="element-34">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-current" data-id="element-35" />)}
            </div>
            <p className="text-white font-semibold text-lg" data-id="element-36">
              4.9/5 · Más de 800 estudiantes ya lo usan
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 font-medium text-sm text-center" data-id="element-37">
              <Flame className="w-4 h-4 shrink-0" data-id="element-38" />
              47 estudiantes obtuvieron acceso hoy
            </div>
          </div>

          <CTAButton subtext="⚡ Acceso Inmediato · 🛡️ Garantía 7 Días · 🌎 Pagá en tu moneda local" scrollTo="oferta" data-id="element-39">
            🧠 ¡SÍ! QUIERO EL MEGA PACK COMPLETO
          </CTAButton>

          <div className="mt-6 flex flex-col items-center gap-3 text-center" data-id="element-40">
            <div className="flex gap-2 text-2xl" data-id="element-41">🩺 🧠 📚 🌎</div>
            <p className="text-sm text-gray-400 max-w-md" data-id="element-42">
              El recurso número 1 para estudiantes de salud en Latinoamérica.
            </p>
          </div>
        </div>

        {/* Opening block — problem */}
        <div className="mb-16" data-id="element-43">
          <div className="glass-panel p-8 md:p-10 rounded-2xl border-gold-500/20 relative overflow-hidden" data-id="element-44">
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-gold-400 to-gold-600" data-id="element-45"></div>
            <Quote className="absolute top-6 right-6 w-24 h-24 text-white/5 rotate-180 pointer-events-none" data-id="element-46" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-5 relative z-10" data-id="element-47">
              Estudiar anatomía hoy no es simple
            </h2>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed relative z-10 font-medium" data-id="element-48">
              Cada semana tenés que memorizar nombres, estructuras y funciones
              desde cero. Cada sistema del cuerpo humano exige un enfoque
              diferente. Los profesores evalúan con preguntas que conectan
              sistemas entre sí. Trabajás duro cada día — pero seguís sintiendo
              que te falta un sistema claro.{' '}
              <span className="text-gold-400 font-bold" data-id="element-49">
                Eso no es culpa tuya.
              </span>{' '}
              Nadie te dio nunca una herramienta que realmente funcione. Hasta
              hoy.
            </p>
          </div>
        </div>

        {/* Testimonials top */}
        <div className="mb-20" data-id="element-50">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-8" data-id="element-51">
            Más de 800 estudiantes ya confían en nosotros
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-52">
            {testimonialsTop.map((t, i) => <TestimonialCard key={i} {...t} data-id="element-53" />)}
          </div>
        </div>

        {/* Comparison */}
        <div className="mb-20" data-id="element-54">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-55">
            El cambio que vas a sentir desde el primer día
          </h2>
          <div className="glass-panel rounded-2xl overflow-hidden" data-id="element-56">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-57">
              <div className="p-6 md:p-8 bg-dark-800/50 border-b md:border-b-0 md:border-r border-white/5" data-id="element-58">
                <h3 className="text-xl font-bold text-gray-400 mb-6 flex items-center gap-2" data-id="element-59">
                  ANTES DEL MEGA PACK
                  <span className="text-sm font-normal text-gray-500 block mt-1" data-id="element-60">
                    (Lo que vivís ahora)
                  </span>
                </h3>
                <ul className="space-y-6" data-id="element-61">
                  {['Estudiás sin orden ni método claro.', 'No sabés qué repasar antes del examen.', 'Memorizás y olvidás todo al día siguiente.', 'Los sistemas del cuerpo te parecen piezas sueltas.', 'Improvisás el estudio cada semana.', 'Llegás al examen con esperanza, no con confianza.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-400" data-id="element-62">
                      <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0" data-id="element-63">
                        <X className="w-4 h-4 text-red-500" data-id="element-64" />
                      </div>
                      <span className="leading-relaxed" data-id="element-65">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div className="p-6 md:p-8 bg-emerald-900/10 relative overflow-hidden" data-id="element-66">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 blur-[80px] rounded-full pointer-events-none blur-glow" data-id="element-67"></div>
                <h3 className="text-xl font-bold text-emerald-400 mb-6 flex items-center gap-2 relative z-10" data-id="element-68">
                  DESPUÉS DEL MEGA PACK
                  <span className="text-sm font-normal text-emerald-500/70 block mt-1" data-id="element-69">
                    (Tu estudio desde hoy)
                  </span>
                </h3>
                <ul className="space-y-6 relative z-10" data-id="element-70">
                  {['Tenés un plan de 14 días listo para usar.', 'Fichas de memorización rápida para los últimos días.', 'Detectás vacíos con 291 preguntas de autoevaluación.', 'Entendés el cuerpo como un sistema integrado.', 'Abrís el PDF y estudiás sin estrés ni pérdida de tiempo.', 'Llegás a cada evaluación con confianza real.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-white font-medium" data-id="element-71">
                      <div className="mt-1 bg-emerald-500/20 p-1 rounded-full shrink-0" data-id="element-72">
                        <Check className="w-4 h-4 text-emerald-400" data-id="element-73" />
                      </div>
                      <span className="leading-relaxed" data-id="element-74">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Para quién es */}
        <div className="mb-20" data-id="element-75">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-76">
            Este sistema es para vos si...
          </h2>
          <div className="glass-panel p-8 rounded-2xl border-white/10" data-id="element-77">
            <ul className="space-y-5" data-id="element-78">
              {['Sos estudiante de medicina, enfermería, fisioterapia, kinesiología, nutrición u odontología.', 'Estás cansado de estudiar sin un método que realmente te organice.', 'Querés dejar de memorizar y empezar a entender el cuerpo humano de verdad.', 'Querés que el estudio funcione bien incluso el día antes del examen.', 'Querés llegar a cada evaluación con confianza real y no con esperanza.', 'Querés desarrollar una base sólida en anatomía para toda tu carrera.'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-79">
                  <div className="mt-1 bg-gold-500/20 p-1.5 rounded-full shrink-0" data-id="element-80">
                    <Check className="w-5 h-5 text-gold-400" data-id="element-81" />
                  </div>
                  <span className="text-lg text-gray-300 leading-relaxed" data-id="element-82">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Differentiators */}
        <div className="mb-20" data-id="element-83">
          <div className="text-center mb-10" data-id="element-84">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-85">
              Por qué este Mega Pack es diferente
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-86">
              Sin teoría vacía. Sin suscripciones mensuales. Sin libros de 800
              páginas imposibles de leer. Solo herramientas listas para usar.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-87">
            {differentiators.map((item, i) => <div key={i} className="glass-panel p-6 rounded-2xl border-white/5 hover:border-white/10 transition-colors" data-id="element-88">
                <div className="w-12 h-12 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center mb-4" data-id="element-89">
                  <item.icon className="w-6 h-6 text-gold-400" data-id="element-90" />
                </div>
                <h3 className="text-lg font-black text-white mb-3 tracking-wider" data-id="element-91">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-92">{item.desc}</p>
              </div>)}
          </div>
        </div>

        {/* Contenido — Guía + Bonos */}
        <div className="mb-20" data-id="element-93">
          <div className="text-center mb-12" data-id="element-94">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-95">
              La Guía Ilustrada Principal <br data-id="element-96" />
              <span className="gold-gradient-text" data-id="element-97">+ 6 Bonos Estratégicos</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-98">
              Recursos digitales en formato PDF — listos para estudiar desde hoy
              en cualquier dispositivo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-99">
            {bonuses.map((item, i) => <div key={i} className={`glass-panel p-7 rounded-2xl relative overflow-hidden group ${i === 0 ? 'md:col-span-2 border-gold-500/30' : 'border-white/5'}`} data-id="element-100">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/5 rounded-full blur-3xl group-hover:bg-gold-500/10 transition-colors blur-glow" data-id="element-101"></div>
                <div className="flex items-center gap-4 mb-4 relative z-10" data-id="element-102">
                  <div className="w-14 h-14 rounded-xl bg-dark-800 border border-white/10 flex items-center justify-center shrink-0" data-id="element-103">
                    <item.icon className="w-7 h-7 text-gold-400" data-id="element-104" />
                  </div>
                  <span className="text-xs font-bold text-gold-400 uppercase tracking-widest bg-gold-500/10 px-3 py-1 rounded-full border border-gold-500/20" data-id="element-105">
                    {item.tag}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-1 relative z-10" data-id="element-106">
                  {item.title}
                </h3>
                <p className="text-gold-400/80 text-sm font-medium mb-3 relative z-10" data-id="element-107">
                  {item.subtitle}
                </p>
                <p className="text-gray-400 leading-relaxed relative z-10 text-sm mb-4" data-id="element-108">
                  {item.desc}
                </p>
                <ul className="space-y-2 relative z-10 mb-4" data-id="element-109">
                  {item.bullets.map((b, bi) => <li key={bi} className="flex items-start gap-2 text-sm text-gray-300" data-id="element-110">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" data-id="element-111" />
                      <span data-id="element-112">{b}</span>
                    </li>)}
                </ul>
                <p className="text-xs text-emerald-400 font-bold relative z-10" data-id="element-113">
                  Valor real: {item.value}
                </p>
              </div>)}
          </div>
        </div>

        {/* Carousel */}
        <div className="mb-24" data-id="element-114">
          <div className="text-center mb-10" data-id="element-115">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-116">
              Mirá el material por dentro
            </h2>
            <p className="text-gray-400 text-lg" data-id="element-117">
              Una vista previa real de lo que vas a encontrar en el Mega Pack.
            </p>
            <p className="text-gold-400 font-medium mt-3" data-id="element-118">
              {currentSlide + 1} / {carouselSlides.length}
            </p>
          </div>

          <div className="relative max-w-2xl mx-auto" data-id="element-119">
            <button onClick={prevSlide} aria-label="Página anterior" className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 rounded-full bg-dark-800 border border-white/10 flex items-center justify-center text-white hover:bg-dark-700 hover:text-gold-400 hover:border-gold-500/30 transition-colors z-20 shadow-xl" data-id="element-120">
              <ChevronLeft className="w-6 h-6" data-id="element-121" />
            </button>

            <button onClick={nextSlide} aria-label="Página siguiente" className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 rounded-full bg-dark-800 border border-white/10 flex items-center justify-center text-white hover:bg-dark-700 hover:text-gold-400 hover:border-gold-500/30 transition-colors z-20 shadow-xl" data-id="element-122">
              <ChevronRight className="w-6 h-6" data-id="element-123" />
            </button>

            <div className="overflow-hidden rounded-2xl glass-panel border-gold-500/20 relative bg-dark-800" data-id="element-124">
              <div className="flex transition-transform duration-500 ease-out" style={{
              transform: `translateX(-${currentSlide * 100}%)`
            }} data-id="element-125">
                {carouselSlides.map((slide, i) => <div key={i} className="w-full shrink-0 relative" data-id="element-126">
                    <img src={slide.image} alt={slide.title} loading="eager" decoding="async" className="w-full h-auto object-contain bg-white block" data-id="element-127" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-6 pt-20" data-id="element-128">
                      <p className="text-xs uppercase tracking-widest text-gold-400 font-bold mb-1" data-id="element-129">
                        {slide.subtitle}
                      </p>
                      <h3 className="text-lg md:text-xl font-bold text-white leading-tight" data-id="element-130">
                        {slide.title}
                      </h3>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-6" data-id="element-131">
              {carouselSlides.map((_, i) => <button key={i} onClick={() => setCurrentSlide(i)} aria-label={`Ir a la página ${i + 1}`} className={`h-2 rounded-full transition-all duration-300 ${i === currentSlide ? 'bg-gold-400 w-8' : 'bg-white/20 hover:bg-white/40 w-2'}`} data-id="element-132" />)}
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div id="oferta" className="mb-16 text-center scroll-mt-8" data-id="element-133">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-400 text-sm font-bold tracking-wide uppercase" data-id="element-134">
            🏆 La opción más elegida en Latinoamérica
          </div>
          <h2 className="text-3xl font-bold text-white mb-10" data-id="element-135">
            La Oferta Completa del Mega Pack
          </h2>

          <div className="inline-block w-full max-w-2xl glass-panel p-8 md:p-12 rounded-3xl border-gold-500/40 relative overflow-hidden text-left shadow-[0_0_50px_rgba(251,191,36,0.05)]" data-id="element-136">
            <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-12 py-2 rotate-45 translate-x-10 translate-y-6 shadow-lg z-20 tracking-wider" data-id="element-137">
              85% OFF
            </div>

            <div className="flex justify-center mb-8" data-id="element-138">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold-500/20 border border-gold-500/30 text-gold-400 text-sm font-bold tracking-wide uppercase" data-id="element-139">
                <Star className="w-4 h-4 fill-current" data-id="element-140" />
                LA OPCIÓN MÁS ELEGIDA
              </div>
            </div>

            <p className="text-gray-400 font-bold uppercase tracking-widest text-sm mb-6 border-b border-white/10 pb-4" data-id="element-141">
              VALOR REAL DE TODO LO QUE RECIBÍS:
            </p>

            <ul className="space-y-4 mb-8" data-id="element-142">
              {[{
              name: 'Guía Principal · 192 páginas',
              price: '$27 USD'
            }, {
              name: 'Bono #1 · Mapa Maestro',
              price: '$15 USD'
            }, {
              name: 'Bono #2 · Guía Express de Términos',
              price: '$15 USD'
            }, {
              name: 'Bono #3 · Checklist por Sistemas',
              price: '$15 USD'
            }, {
              name: 'Bono #4 · Plan de 14 Días',
              price: '$15 USD'
            }, {
              name: 'Bono #5 · Cuaderno de Autoevaluación',
              price: '$15 USD'
            }, {
              name: 'Bono #6 · Fichas de Memorización',
              price: '$15 USD'
            }, {
              name: 'Bono Sorpresa',
              price: '$15 USD'
            }].map((item, i) => <li key={i} className="flex justify-between items-center text-gray-300" data-id="element-143">
                  <span className="flex items-center gap-2" data-id="element-144">
                    <Check className="w-4 h-4 text-emerald-500" data-id="element-145" />
                    {item.name}
                  </span>
                  <span className="font-medium" data-id="element-146">{item.price}</span>
                </li>)}
            </ul>

            <div className="border-t border-white/10 pt-6 mb-8 flex justify-between items-center" data-id="element-147">
              <span className="text-gray-400 font-medium" data-id="element-148">
                VALOR REAL TOTAL:
              </span>
              <span className="text-xl text-gray-500 line-through decoration-red-500/50 decoration-2 font-bold" data-id="element-149">
                $132 USD
              </span>
            </div>

            <div className="text-center space-y-2 mb-8 bg-dark-900/50 p-6 rounded-2xl border border-white/5" data-id="element-150">
              <p className="text-gold-400 font-bold tracking-widest uppercase text-sm" data-id="element-151">
                SOLO HOY
              </p>
              <div className="flex items-start justify-center gap-1" data-id="element-152">
                <span className="text-3xl font-bold text-white mt-2" data-id="element-153">$</span>
                <span className="text-7xl font-black text-white tracking-tighter" data-id="element-154">
                  19
                </span>
                <span className="text-3xl font-bold text-white mt-2" data-id="element-155">.90</span>
                <span className="text-xl font-bold text-gray-400 mt-4 ml-2" data-id="element-156">
                  USD
                </span>
              </div>
              <p className="text-sm text-gray-400 mt-2" data-id="element-157">
                Pago único · Sin cargos mensuales
              </p>
              <div className="inline-flex items-center gap-2 mt-3 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold tracking-wide uppercase" data-id="element-158">
                🌎 Pagalo en tu moneda local
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-159">
                ARS · COP · MXN · CLP · PEN · UYU · y más
              </p>
            </div>

            <div className="flex flex-col items-center gap-2 text-sm text-gray-400 font-medium mb-8 text-center" data-id="element-160">
              <p data-id="element-161">💳 Pago único · ⚡ Acceso inmediato</p>
              <p className="text-red-400 flex items-center gap-1.5 font-semibold" data-id="element-162">
                <Clock className="w-4 h-4" data-id="element-163" /> Solo para los primeros 200
                estudiantes — después vuelve al precio regular
              </p>
            </div>

            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="w-full group relative inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-white transition-all duration-200 bg-gradient-to-b from-emerald-400 to-emerald-600 border-b-4 border-emerald-800 rounded-xl hover:from-emerald-300 hover:to-emerald-500 hover:-translate-y-1 active:translate-y-0 active:border-b-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 focus:ring-offset-dark-900 shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_60px_rgba(16,185,129,0.5)] no-underline" data-id="element-164">
              <span className="flex flex-col items-center gap-1 text-shadow-sm" data-id="element-165">
                <span className="flex items-center gap-2 text-xl tracking-wide uppercase" data-id="element-166">
                  🧠 QUIERO EL MEGA PACK · $19.90 USD
                </span>
                <span className="text-xs font-semibold tracking-wider uppercase text-emerald-100/90" data-id="element-167">
                  🌎 Pagalo en tu moneda local
                </span>
              </span>
            </a>
            <p className="mt-4 text-sm text-center text-gray-400 font-medium" data-id="element-168">
              ⚡ Acceso Inmediato · 💳 Un Solo Pago · 🛡️ Garantía 7 Días
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3 text-xs text-gray-400 font-medium" data-id="element-169">
              <div className="flex items-center gap-2 justify-center bg-dark-800/50 rounded-lg p-2 border border-white/5" data-id="element-170">
                🔒 Compra segura
              </div>
              <div className="flex items-center gap-2 justify-center bg-dark-800/50 rounded-lg p-2 border border-white/5" data-id="element-171">
                📥 Descarga inmediata
              </div>
              <div className="flex items-center gap-2 justify-center bg-dark-800/50 rounded-lg p-2 border border-white/5" data-id="element-172">
                🌎 Pagá en tu moneda local
              </div>
              <div className="flex items-center gap-2 justify-center bg-dark-800/50 rounded-lg p-2 border border-white/5" data-id="element-173">
                🛡️ Garantía 7 días
              </div>
            </div>
          </div>
        </div>

        {/* Guarantee */}
        <div className="max-w-3xl mx-auto glass-panel p-8 md:p-10 rounded-3xl flex flex-col md:flex-row items-center gap-8 text-center md:text-left border-emerald-500/30 mb-20 relative overflow-hidden" data-id="element-174">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none blur-glow" data-id="element-175"></div>
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 border border-emerald-500/20 relative z-10" data-id="element-176">
            <ShieldCheck className="w-10 h-10 text-emerald-400" data-id="element-177" />
          </div>
          <div className="relative z-10" data-id="element-178">
            <h3 className="text-2xl font-bold text-white mb-3" data-id="element-179">
              GARANTÍA DE DEVOLUCIÓN 100% EN 7 DÍAS
            </h3>
            <p className="text-gray-300 text-base leading-relaxed" data-id="element-180">
              Descargá todo. Leé todo. Aplicalo en tu próxima sesión de estudio.
              Si en 7 días no estás 100% satisfecho te devolvemos todo tu
              dinero.{' '}
              <span className="text-white font-bold" data-id="element-181">
                Sin preguntas. Sin trámites complicados.
              </span>{' '}
              Todo el riesgo lo asumimos nosotros.
            </p>
          </div>
        </div>

        {/* Mission */}
        <div className="max-w-3xl mx-auto mb-20 text-center cv-auto" data-id="element-182">
          <div className="relative w-72 h-56 mx-auto mb-8" data-id="element-183">
            <div className="absolute inset-0 bg-gold-500/10 blur-[60px] rounded-full pointer-events-none blur-glow" data-id="element-184"></div>
            <img src={HERO_IMAGE} alt="Mega Pack · Anatomía Humana" width={1200} height={900} loading="lazy" className="relative z-10 w-full h-full object-contain drop-shadow-2xl" data-id="element-185" />
          </div>
          <h3 className="text-xl font-bold text-white mb-6 uppercase tracking-widest" data-id="element-186">
            Mega Pack · Nuestra misión
          </h3>
          <p className="text-xl md:text-2xl text-gray-400 italic leading-relaxed font-serif" data-id="element-187">
            "Creamos este sistema para estudiantes de salud en Latinoamérica que
            trabajan duro pero todavía no ven los resultados que desean. Sin
            teoría vacía. Sin libros imposibles de leer. Solo herramientas que
            realmente funcionan — para que puedas estudiar anatomía como un
            verdadero profesional de la salud."
          </p>
          <p className="text-gold-400 font-bold mt-4" data-id="element-188">
            — El equipo de Mega Pack · Anatomía Humana
          </p>
        </div>

        {/* Comments */}
        <div className="cv-auto" data-id="element-189">
          <Suspense fallback={<div className="h-[600px]" data-id="element-191" />} data-id="element-190">
            <CommentsSection data-id="element-192" />
          </Suspense>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mb-20 cv-auto" data-id="element-193">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-194">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-4" data-id="element-195">
            {faqs.map((faq, index) => <div key={index} className="glass-panel rounded-xl border-white/5 overflow-hidden transition-all duration-300" data-id="element-196">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none hover:bg-white/[0.02] transition-colors" data-id="element-197">
                  <span className="text-lg font-semibold text-white pr-4" data-id="element-198">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gold-400 shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-199" />
                </button>
                <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === index ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-200">
                  <p className="text-gray-400 leading-relaxed border-t border-white/5 pt-4" data-id="element-201">
                    {faq.a}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Final CTA */}
        <div className="mb-10 text-center" data-id="element-202">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-203">
            ¿Estás listo para cambiar tu forma de estudiar anatomía?
          </h2>
          <p className="text-gray-400 text-lg mb-2" data-id="element-204">
            Sumate a más de 800 estudiantes que ya eligieron este sistema
          </p>
          <CTAButton subtext="Solo $19.90 USD · Pago único · 🌎 Pagá en tu moneda local" scrollTo="oferta" data-id="element-205">
            🧠 SÍ · QUIERO EL MEGA PACK AHORA
          </CTAButton>
          <p className="text-center text-gray-400 max-w-xl mx-auto mt-4 text-sm" data-id="element-206">
            ✅ Acceso inmediato · ✅ Garantía 7 días · ✅ Pago único para
            siempre
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-white/5 pt-12 pb-8 px-4 text-center" data-id="element-207">
        <div className="max-w-3xl mx-auto space-y-6" data-id="element-208">
          <div className="flex items-center justify-center gap-2 text-white font-bold text-lg" data-id="element-209">
            <Stethoscope className="w-5 h-5 text-gold-400" data-id="element-210" /> Mega Pack ·
            Anatomía Humana
          </div>
          <p className="text-gray-400 text-sm leading-relaxed max-w-xl mx-auto" data-id="element-211">
            La mejor forma de estudiar anatomía sin improvisar ni perder el
            tiempo. Herramientas prácticas para estudiantes que quieren
            resultados concretos.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-gray-500" data-id="element-212">
            <a href="#" className="hover:text-gold-400 transition-colors" data-id="element-213">
              🔒 Política de privacidad
            </a>
            <a href="#" className="hover:text-gold-400 transition-colors" data-id="element-214">
              Términos y condiciones
            </a>
            <a href="#" className="hover:text-gold-400 transition-colors" data-id="element-215">
              Política de cookies
            </a>
          </div>
          <p className="text-gray-600 text-xs pt-4" data-id="element-216">
            © 2026 Mega Pack · Anatomía Humana — Todos los derechos reservados.
            Producto digital · PDF de descarga inmediata.
          </p>
          <p className="text-gray-600 text-[11px] max-w-2xl mx-auto leading-relaxed pt-2" data-id="element-217">
            Los resultados descritos son ejemplos reales pero no están
            garantizados. Los resultados individuales pueden variar según el
            uso. Este producto no reemplaza la enseñanza académica formal.
          </p>
        </div>
      </footer>

      <Suspense fallback={null} data-id="element-218">
        <SocialProofPopup data-id="element-219" />
      </Suspense>
    </div>;
}